
import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription
} from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useEventEditor } from './useEventEditor';
import { useEventSubmit } from './useEventSubmit';
import EventEditorTabContent from './EventEditorTabContent';
import EventEditorLoading from './EventEditorLoading';
import { EventFormValues } from '../schemas/eventFormSchema';

interface EventEditorModalProps {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
  onEventUpdated: () => void;
}

const EventEditorModal = ({ eventId, open, onClose, onEventUpdated }: EventEditorModalProps) => {
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const {
    isLoading,
    form,
    resetForm
  } = useEventEditor({ 
    eventId, 
    open, 
    onClose,
    onFormChanged: (isDirty) => setIsFormDirty(isDirty)
  });

  const {
    isSubmitting,
    handleSubmit,
  } = useEventSubmit({ 
    eventId, 
    onEventUpdated, 
    onClose: () => {
      resetForm();
      setIsFormDirty(false);
      onClose();
    }
  });

  const handleCloseAttempt = () => {
    if (isFormDirty) {
      setShowUnsavedChangesAlert(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesAlert(false);
    resetForm();
    setIsFormDirty(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesAlert(false);
  };

  const submitHandler = async (values: EventFormValues) => {
    console.log('EventEditorModal submitting with values:', values);
    try {
      await handleSubmit(values);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  if (isLoading) {
    return <EventEditorLoading />;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && handleCloseAttempt()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-white pb-2 border-b">
            <DialogTitle>Esemény szerkesztése</DialogTitle>
            <DialogDescription>
              Módosítsa az esemény adatait, majd kattintson a Mentés gombra a változtatások mentéséhez.
            </DialogDescription>
          </DialogHeader>

          <EventEditorTabContent 
            form={form} 
            isSubmitting={isSubmitting}
            onSubmit={submitHandler}
            onClose={handleCloseAttempt}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={showUnsavedChangesAlert} onOpenChange={setShowUnsavedChangesAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nem mentett változtatások</AlertDialogTitle>
            <AlertDialogDescription>
              Nem mentette el a változtatásokat. Biztosan szeretné bezárni az űrlapot? A nem mentett változtatások elvesznek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Mégse</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>Bezárás mentés nélkül</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EventEditorModal;
